<template>

    <Toast/>

    <MenuHealthCompact/>

    <div class="px-6 surface-overlay relative">

        <ul class="list-none py-2 px-0 m-0 flex align-items-center font-medium overflow-x-auto w-full">
            <li class="pr-3"><a class="cursor-pointer">Financial Clarity</a></li>
            <li class="px-2"><i class="pi pi-angle-right text-900"></i></li>
            <li class="px-2"><a class="cursor-pointer text-900 white-space-nowrap">Claims</a></li>
        </ul>

        <div v-show="activeTab === 1" class="grid">
            <div class="col-12 md:col-12">
                <div class="card p-fluid">

                    <h3>Manage Claims</h3>

                    <DataTable
                        v-model:lazyParams="lazyParams"
                        :value="claims"
                        lazy
                        paginator
                        size="small"
                        :ptOptions="{ mergeSections: true, classNameMergeStrategy: 'replace' }"
                        :pt="{headerCell: { class: 'py-1 px-2' }, bodyCell: { class: 'p-0' }, bodyRow: { class: 'p-0' }}"
                        :rows="10"
                        :totalRecords="totalRecords"
                        :loading="loading"
                        :rowsPerPageOptions="[10, 20, 50, 100]"
                        :sortMode="'single'"
                        :rowHover="true"
                        :striped-rows="true"
                        :reorderableColumns="true"
                        filterDisplay="menu"
                        responsiveLayout="scroll"
                        :globalFilterFields="['id', 'claimStartDatetime', 'clientName', 'clientPreferredName', 'responseReference', 'invoiceUniqueId']"
                        @page="onPage"
                        @sort="onSort"
                        @filter="onFilter">

                        <template #header>
                            <div class="p-grid">
                                <div class="grid">
                                    <div class="col-12 lg:col-6 p-2">
                                        <MultiSelect :modelValue="selectedColumns"
                                                     :options="columnsFullList"
                                                     optionLabel="header"
                                                     @update:modelValue="onToggleColumns"
                                                     display="chip"
                                                     placeholder="Select Columns"/>
                                    </div>
                                    <div class="col-12 lg:col-6 p-2">
                                        <Dropdown v-model="selectedStandardFilter"
                                                  :options="statusTypesLimited"
                                                  optionLabel="name" optionValue="code"
                                                  placeholder="Select a Filter"
                                                  @update:modelValue="onToggleStandardFilters"/>
                                    </div>

                                </div>

                                <div class="grid">
                                    <div class="col-12 lg:col-6 p-2">
                                        <Button type="button" icon="pi pi-filter-slash" label="Clear Search"
                                                class="p-button-outlined p-ml-2" @click="clearFilter()"/>
                                    </div>
                                    <div class="col-12 lg:col-6 p-2">
                                        <Button type="button" icon="pi pi-replay"
                                                label="Restore to Original View"
                                                class="p-button-outlined" @click="restoreToOriginal()"/>
                                    </div>
                                </div>

                                <div class="p-col-12 p-md-2">
                                                <span class="p-input-icon-left">
                                                    <i class="pi pi-search"/>
                                                    <InputText v-model="filters['global'].value"
                                                               placeholder="Keyword Search on Payment ID, Invoice ID, Client Name, Client Preferred Name, Payment Date, Description or Amount (Please press enter to search)"
                                                               style="width: 100%"
                                                               @keyup.enter="onSearch"/>

                                                </span>
                                </div>
                            </div>
                        </template>

                        <template #empty>
                            No claims found.
                        </template>

                        <template #loading>
                            Loading claims. Please wait.
                        </template>

                        <Column
                            field="selectAnOption"
                            header=""
                            :headerStyle="{ width: '2rem' }"
                            :bodyStyle="{ width: '2rem', padding: '0.25rem' }">
                            <template #body="{ data, index }">
                                <Button icon="pi pi-ellipsis-v"
                                        class="p-button-sm p-button-text p-button-rounded m-0"
                                        @click="$refs['menu' + index].toggle($event)"/>
                                <Menu
                                    :ref="'menu' + index"
                                    :model="getFilteredItems(data)"
                                    :popup="true"/>
                            </template>
                        </Column>

                        <Column field="id" header="Claim #" :sortable="true"/>
                        <Column field="claimStartDatetime" header="Claim Date" :sortable="true"/>

                        <template v-for="col in selectedColumns" :key="col.field">

                            <Column v-if="col.field === 'status'" :field="col.field" :header="col.header"
                                    :sortable="col.sortable">
                                <template #body="{data}">
                                    <span class="p-column-title">Status</span>
                                    <span v-if="data.status === 'I'"
                                          :class="'claim-badge status-' + data.status">Initiated</span>
                                    <span v-if="data.status === 'A'"
                                          :class="'claim-badge status-' + data.status">Approved</span>
                                    <span v-if="data.status === 'D'"
                                          :class="'claim-badge status-' + data.status">Partially Approved</span>
                                    <span v-if="data.status === 'P'"
                                          :class="'claim-badge status-' + data.status">Paid</span>
                                    <span v-if="data.status === 'R'"
                                          :class="'claim-badge status-' + data.status">Rejected</span>
                                    <span v-if="data.status === 'S'"
                                          :class="'claim-badge status-' + data.status">Submitted</span>
                                    <span v-if="data.status === 'V'"
                                          :class="'claim-badge status-' + data.status">Validated</span>
                                    <span v-if="data.status === 'N'"
                                          :class="'claim-badge status-' + data.status">Not Validated</span>
                                </template>
                            </Column>

                            <Column v-else-if="col.field === 'type'" :field="col.field" :header="col.header"
                                    :sortable="col.sortable">
                                <template #body="{data}">
                                    <span class="p-column-title">Submission Method</span>
                                    <span v-if="data.healthInsuranceSubmission === 'E'"
                                          :class="'submission-badge status-' + data.healthInsuranceSubmission">Email</span>
                                    <span v-if="data.healthInsuranceSubmission === 'D'"
                                          :class="'submission-badge status-' + data.healthInsuranceSubmission">EDI</span>
                                </template>
                            </Column>

                            <Column v-else-if="col.field === 'amountClaimed'" :field="col.field" :header="col.header"
                                    :sortable="col.sortable">
                                <template #body="{data}">
                                    <span class="p-column-title">Amount Claimed</span>
                                    {{ formatAmount(data.amountClaimed) }}
                                </template>
                            </Column>

                            <Column v-else-if="col.field === 'amountPaid'" :field="col.field" :header="col.header"
                                    :sortable="col.sortable">
                                <template #body="{data}">
                                    <span class="p-column-title">Amount Paid</span>
                                    {{ formatAmount(data.amountPaid) }}
                                </template>
                            </Column>

                            <Column v-else :field="col.field" :header="col.header" :sortable="col.sortable"/>

                        </template>

                    </DataTable>

                </div>
            </div>
        </div>


        <div v-show="activeTab === 2" class="grid">
            <div class="col-12 md:col-12">
                <div class="card p-fluid">

                    <h3>View Response of Claim for Invoice #{{ claim.invoiceUniqueId }} of {{ claim.clientName }}</h3>

                    <div class="field">
                        <label for="response">Response</label>
                        <Textarea placeholder="Your Message" rows="5" cols="30" id="response"
                                  v-model.trim="claim.responseDescription" required="false" disabled="true"/>
                    </div>

                    <Button label="Cancel" class="mr-2 mb-2" :loading="loadingResponse" @click="callReset"></Button>

                </div>
            </div>
        </div>


        <div v-show="activeTab === 3" class="grid">
            <div class="col-12 md:col-12">
                <div class="card p-fluid">

                    <h3>Update Claim for Invoice #{{ claim.invoiceUniqueId }} of {{ claim.clientName }}</h3>

                    <div class="field">
                        <label for="responseReference">Response Reference</label>
                        <InputText id="responseReference"
                                   v-model.trim="claim.responseReference"
                                   required="true"/>
                    </div>

                    <Button label="Update" class="mr-2 mb-2" :loading="loadingResponse" @click="updateClaim"></Button>
                    <Button label="Cancel" class="mr-2 mb-2" :loading="loadingResponse" @click="callReset"></Button>

                </div>
            </div>
        </div>


        <div v-show="activeTab === 4" class="grid">
            <div class="col-12 md:col-12">
                <div class="card p-fluid">

                    <h3>Submit Claim for Invoice #{{ claim.invoiceUniqueId }} of {{ claim.clientName }}</h3>

                    <div class="field">
                        <label for="confirmationResponse">Do You Wish to Submit this Claim?</label>
                        <Dropdown v-model="confirmationResponse" :options="confirmationTypes"
                                  optionLabel="name" optionValue="code"
                                  placeholder="Select a Response"/>
                    </div>

                    <div v-show="( (confirmationResponse==='Y') && (claim.healthInsuranceSubmission==='E'))"
                         class="field">
                        <label for="resubmissionResponse">Is This a Claim Resubmission?</label>
                        <Dropdown v-model="resubmissionResponse" :options="confirmationTypes"
                                  optionLabel="name" optionValue="code"
                                  placeholder="Select a Response"/>
                    </div>

                    <div v-show="( (confirmationResponse==='Y') && (claim.healthInsuranceSubmission==='E'))"
                         style="height: 15px;"></div>

                    <div v-show="( (confirmationResponse==='Y') && (claim.healthInsuranceSubmission==='E'))"
                         class="p-field">
                        <label for="file">File Attachment</label>
                        <div style="height: 10px;"></div>
                        <input
                            type="file"
                            ref="fileInput"
                            @change="handleEmailFileUpload"
                            accept="application/pdf"/>
                    </div>

                    <div style="height: 15px;"></div>

                    <Button label="Submit" class="mr-2 mb-2" :loading="loadingResponse" @click="submitClaim"></Button>
                    <Button label="Cancel" class="mr-2 mb-2" :loading="loadingResponse" @click="callReset"></Button>

                </div>
            </div>
        </div>


        <div v-show="activeTab === 5" class="grid">
            <div class="col-12 md:col-12">
                <div class="card p-fluid">

                    <h3>Submit Benefit Check for Claim for Invoice #{{ claim.invoiceUniqueId }} of
                        {{ claim.clientName }}</h3>

                    <div class="field">
                        <label for="confirmationResponse">Do You Wish to Submit a Benefit Check for this Claim?</label>
                        <Dropdown v-model="confirmationResponse" :options="confirmationTypes"
                                  optionLabel="name" optionValue="code"
                                  placeholder="Select a Response"/>
                    </div>

                    <Button label="Submit" class="mr-2 mb-2" :loading="loadingResponse"
                            @click="submitBenefitCheck"></Button>
                    <Button label="Cancel" class="mr-2 mb-2" :loading="loadingResponse" @click="callReset"></Button>

                </div>
            </div>
        </div>


        <div v-show="activeTab === 6" class="grid">
            <div class="col-12 md:col-12">
                <div class="card p-fluid">

                    <h3>Delete Claim for Invoice #{{ claim.invoiceUniqueId }} of {{ claim.clientName }}</h3>

                    <div class="field">
                        <label for="confirmationResponse">Do You Wish to Delete this Claim?</label>
                        <Dropdown v-model="confirmationResponse" :options="confirmationTypes"
                                  optionLabel="name" optionValue="code"
                                  placeholder="Select a Response"/>
                    </div>

                    <Button label="Delete" class="mr-2 mb-2" :loading="loadingResponse" @click="deleteClaim"></Button>
                    <Button label="Cancel" class="mr-2 mb-2" :loading="loadingResponse" @click="callReset"></Button>

                </div>
            </div>
        </div>


        <div v-show="activeTab === 7" class="grid">
            <div class="col-12 md:col-12">
                <div class="card p-fluid">

                    <h3>Reverse Claim for Invoice #{{ claim.invoiceUniqueId }} of {{ claim.clientName }}</h3>

                    <div class="field">
                        <label for="confirmationResponse">Do You Wish to Submit a Reversal for this Claim?</label>
                        <Dropdown v-model="confirmationResponse" :options="confirmationTypes"
                                  optionLabel="name" optionValue="code"
                                  placeholder="Select a Response"/>
                    </div>

                    <Button label="Submit" class="mr-2 mb-2" :loading="loadingResponse"
                            @click="submitClaimReversal"></Button>
                    <Button label="Cancel" class="mr-2 mb-2" :loading="loadingResponse" @click="callReset"></Button>

                </div>
            </div>
        </div>


        <div v-show="activeTab === 8" class="grid">
            <div class="col-12 md:col-12">
                <div class="card p-fluid">

                    <h3>Update Status of Claim for Invoice #{{ claim.invoiceUniqueId }} of {{ claim.clientName }}</h3>

                    <div class="field">
                        <label for="invoiceStatus">Select the status?</label>
                        <Dropdown v-model="claim.status" :options="claimStatusTypes"
                                  optionLabel="name" optionValue="code"
                                  placeholder="Select a Status"/>
                    </div>

                    <Button label="Update" class="mr-2 mb-2" :loading="loadingResponse"
                            @click="updateClaimStatus"></Button>
                    <Button label="Cancel" class="mr-2 mb-2" :loading="loadingResponse" @click="callReset"></Button>

                </div>
            </div>
        </div>

    </div>


    <IsoEvolveFooter/>

</template>

<script>
import IsoEvolveFooter from "@/components/heading/authentication/IsoEvolveFooter.vue";
import MenuHealthCompact from "@/components/menu/health/MenuHealthCompact.vue";
import {useUserStore} from "@/stores/UserStore";
import UserService from "@/service/UserService";
import {FilterMatchMode, FilterOperator} from "primevue/api";
import CompanyService from "@/service/CompanyService";
import ClaimService from "@/service/ClaimService";
import numeral from "numeral";

export default {
    components: {
        IsoEvolveFooter, MenuHealthCompact
    },
    setup() {
        const userStore = useUserStore()

        return {
            userStore
        }
    },
    created() {
        this.initFilters();

        this.columnsDefault = [
            {field: 'invoiceUniqueId', header: 'Invoice #', sortable: true},
            {field: 'amountClaimed', header: 'Amount Claimed', sortable: true},
            {field: 'amountPaid', header: 'Amount Paid', sortable: true},
            {field: 'status', header: 'Status', sortable: true},
            {field: 'type', header: 'Type', sortable: true},
            {field: 'clientName', header: 'Client Name', sortable: true},
            {field: 'medicalName', header: 'Health Insurance Company', sortable: true}
        ];

        this.columnsFullList = [
            {field: 'invoiceUniqueId', header: 'Invoice #', sortable: true},
            {field: 'amountClaimed', header: 'Amount Claimed', sortable: true},
            {field: 'amountPaid', header: 'Amount Paid', sortable: true},
            {field: 'status', header: 'Status', sortable: true},
            {field: 'type', header: 'Type', sortable: true},
            {field: 'clientName', header: 'Client Name', sortable: true},
            {field: 'clientPreferredName', header: 'Client Preferred Name', sortable: true},
            {field: 'medicalName', header: 'Health Insurance Company', sortable: true},
            {field: 'responseReference', header: 'Response Reference', sortable: true},
            {field: 'practitionerName', header: 'Practitioner Name', sortable: true}
        ];

        this.selectedColumns = this.columnsDefault;

    },
    async mounted() {
        try {
            await UserService.getUserPing();
        } catch (e) {
            this.userStore.clearUser();
            this.$router.push("login");
            return;
        }

        if (!this.userStore.user.id) {
            this.userStore.clearUser();
            this.$router.push('login');
            return;
        }

        await this.loadCompanyContext();
    },
    data() {

        return {
            totalRecords: 0,
            loading: false,
            lazyParams: {
                first: 0,
                rows: 10,
                sortField: null,
                sortOrder: null,
                filters: {}
            },
            profile: {},
            activeTab: 1,
            companyConfiguration: {},
            userConfiguration: {},
            file: null,
            filters: null,
            loadingResponse: false,
            submitted: false,
            filteredItemsCache: new Map(),
            claim: {},
            claims: null,
            originalClaims: null,
            confirmationResponse: 'N',
            resubmissionResponse: 'N',
            selectedColumns: null,
            columnsDefault: null,
            columnsFullList: null,
            selectedStandardFilter: null,
            standardFilters: null,
            confirmationTypes: [
                {name: 'Yes', code: 'Y'},
                {name: 'No', code: 'N'}
            ],
            statusTypes: [
                {name: 'Active', code: 'A'},
                {name: 'Inactive', code: 'I'}
            ],
            claimStatusTypes: [
                {name: 'Approved', code: 'A'},
                {name: 'Partially Approved', code: 'D'},
                {name: 'Paid', code: 'P'},
                {name: 'Rejected', code: 'R'},
                {name: 'Submitted', code: 'S'},
                {name: 'Initiated', code: 'I'},
                {name: 'Validated', code: 'V'},
                {name: 'Not Validated', code: 'N'}
            ],
            statusTypesLimited: [
                {name: 'Approved', code: 'A'},
                {name: 'Partially Approved', code: 'D'},
                {name: 'Paid', code: 'P'},
                {name: 'Rejected', code: 'R'},
                {name: 'Submitted', code: 'S'},
                {name: 'Initiated', code: 'I'},
                {name: 'Email', code: 'E'},
                {name: 'EDI', code: 'Z'}
            ],
        }
    },
    watch: {
        claims() {
            this.filteredItemsCache.clear();
        },
        'userStore.activeCompanyId': {
            immediate: false,
            async handler(newCompanyId, oldCompanyId) {
                if (!newCompanyId || newCompanyId === oldCompanyId) {
                    return;
                }

                await this.loadCompanyContext();
            }
        }
    },
    methods: {
        async loadCompanyContext() {
            try {
                const userData = await UserService.getUser(this.userStore.user.id);
                this.profile = userData.data;
            } catch (e) {
                this.userStore.clearUser();
                this.$router.push('login');
                return;
            }

            this.profile.companyId = this.userStore.activeCompanyId;

            const [userConfigurationData, companyConfigurationData] = await Promise.all([
                UserService.getUserConfig(this.userStore.user.id),
                CompanyService.getCompanyConfiguration(this.profile.companyId)
            ]);

            this.userConfiguration = userConfigurationData.data;
            this.companyConfiguration = companyConfigurationData.data;

            await this.loadData();

            this.loading = false;

        },
        async loadData() {
            this.loading = true;
            try {
                if (this.userStore.user.rights === 'M') {
                    const response = await ClaimService.getCompanyPractitionerClaimsLazyLoading(
                        this.profile.companyId,
                        this.userStore.user.practitionerId,
                        this.lazyParams,
                        this.filters['global']?.value || '',
                        this.selectedStandardFilter, null, null);

                    this.claims = Array.isArray(response.data.content) ? response.data.content : [];
                    this.originalClaims = this.claims;

                    this.totalRecords = response.data?.totalElements || 0;

                } else {
                    const response = await ClaimService.getCompanyClaimsLazyLoading(
                        this.profile.companyId,
                        this.lazyParams,
                        this.filters['global']?.value || '',
                        this.selectedStandardFilter, null, null);

                    this.claims = Array.isArray(response.data.content) ? response.data.content : [];
                    this.originalClaims = this.claims;

                    this.totalRecords = response.data?.totalElements || 0;
                }

            } catch (error) {
                this.claims = [];
                this.originalClaims = [];
                this.totalRecords = 0;
            }
            this.loading = false;
        },
        async onPage(event) {
            this.lazyParams.first = event.first;
            this.lazyParams.rows = event.rows;
            await this.loadData();
        },
        async onSort(event) {
            this.lazyParams.sortField = event.sortField;
            this.lazyParams.sortOrder = event.sortOrder;
            await this.loadData();
        },
        async onFilter(event) {
            this.lazyParams.filters = event.filters;
            await this.loadData();
        },
        async onSearch() {
            this.lazyParams.first = 0;
            await this.loadData();
        },
        async onToggleStandardFilters() {
            this.lazyParams.first = 0;
            await this.loadData();
        },
        async clearFilter() {
            this.initFilters();
            this.selectedStandardFilter = null;
            this.lazyParams.filters = {};
            this.lazyParams.first = 0;
            this.lazyParams.rows = 10;
            await this.loadData();
        },
        async restoreToOriginal() {
            this.initFilters();
            this.selectedColumns = this.columnsDefault;
            this.selectedStandardFilter = null;
            this.lazyParams.filters = {};
            this.lazyParams.first = 0;
            this.lazyParams.rows = 10;
            await this.loadData();
        },
        initFilters() {
            this.filters = {
                'global': {value: null, matchMode: FilterMatchMode.CONTAINS},
                'id': {
                    operator: FilterOperator.AND,
                    constraints: [{value: null, matchMode: FilterMatchMode.STARTS_WITH}]
                },
                'claimStartDatetime': {
                    operator: FilterOperator.AND,
                    constraints: [{value: null, matchMode: FilterMatchMode.STARTS_WITH}]
                },
                'clientName': {
                    operator: FilterOperator.AND,
                    constraints: [{value: null, matchMode: FilterMatchMode.STARTS_WITH}]
                },
                'clientPreferredName': {
                    operator: FilterOperator.AND,
                    constraints: [{value: null, matchMode: FilterMatchMode.STARTS_WITH}]
                },
                'responseReference': {
                    operator: FilterOperator.AND,
                    constraints: [{value: null, matchMode: FilterMatchMode.STARTS_WITH}]
                },
                'invoiceUniqueId': {
                    operator: FilterOperator.AND,
                    constraints: [{value: null, matchMode: FilterMatchMode.STARTS_WITH}]
                }
            }
        },
        callReset() {
            this.loadingResponse = true;
            this.activeTab = 1;
            this.loadingResponse = false;
        },
        callViewResponse(data) {
            this.activeTab = 2;
            this.claim = data;

            this.confirmationResponse = 'N';
            this.resubmissionResponse = 'N';

            // const jsonObject = JSON.parse(this.claim.response);
            // this.jsonResponse = JSON.stringify(jsonObject, null, 2);

            // this.jsonResponse = this.claim.responseDescription;
        },
        callUpdateClaim(data) {
            this.activeTab = 3;
            this.claim = data;
        },
        callSubmitClaim(data) {
            this.activeTab = 4;
            this.claim = data;
            this.confirmationResponse = 'N';
        },
        callSubmitBenefitCheck(data) {
            this.activeTab = 5;
            this.claim = data;
            this.confirmationResponse = 'N';
        },
        callDeleteClaim(data) {
            this.activeTab = 6;
            this.claim = data;
            this.confirmationResponse = 'N';
        },
        callReverseClaim(data) {
            this.activeTab = 7;
            this.claim = data;
        },
        callUpdateStatus(data) {
            this.activeTab = 8;
            this.claim = data;
        },
        resetClientFileInput() {
            this.file = null;
            this.$refs.fileInput.value = ''; // clears input element
        },
        handleEmailFileUpload(event) {
            const file = event.target.files[0];

            if (!file) {
                this.file = null;
                return;
            }

            const allowedTypes = [
                'application/pdf'
            ];

            if (!allowedTypes.includes(file.type)) {
                this.$toast.add({
                    severity: 'error',
                    summary: 'Invalid file type',
                    detail: 'Only PDF files are allowed.',
                    life: 3000
                });
                this.resetClientFileInput();
                return;
            }

            this.file = file;
        },
        onToggleColumns(value) {
            const selectedFields = value.map(v => v.field);
            this.selectedColumns = this.columnsFullList.filter(col =>
                selectedFields.includes(col.field)
            );
        },
        getFilteredItems(data) {
            if (this.filteredItemsCache.has(data.id)) {
                return this.filteredItemsCache.get(data.id);
            }

            const items = [];

            if ((data.status === 'A') || (data.status === 'D') || (data.status === 'R') ||
                (data.status === 'V') || (data.status === 'N')) {
                items.push({
                    label: 'View Response',
                    class: 'mb-4 mr-4 ml-4 mt-4',
                    command: () => {
                        this.callViewResponse(data);
                    }
                });

            }

            if (data.healthInsuranceSubmission === 'E') {
                items.push({
                    label: 'Update',
                    class: 'mb-4 mr-4 ml-4 mt-4',
                    command: () => {
                        this.callUpdateClaim(data);
                    }
                });

            }

            if ((data.status === 'I') || (data.status === 'S') || (data.status === 'R') ||
                (data.status === 'V') || (data.status === 'N')) {
                items.push({
                    label: 'Submit',
                    class: 'mb-4 mr-4 ml-4 mt-4',
                    command: () => {
                        this.callSubmitClaim(data);
                    }
                });

            }

            if ((data.healthInsuranceSubmission == 'D') &&
                ((data.status === 'I') || (data.status === 'S') || (data.status === 'R') ||
                    (data.status === 'V') || (data.status === 'N'))) {
                items.push({
                    label: 'Benefit Check',
                    class: 'mb-4 mr-4 ml-4 mt-4',
                    command: () => {
                        this.callSubmitBenefitCheck(data);
                    }
                });

            }

            if ((data.status === 'I') || (data.status === 'S') || (data.status === 'R') ||
                (data.status === 'V') || (data.status === 'N')) {
                items.push({
                    label: 'Delete',
                    class: 'mb-4 mr-4 ml-4 mt-4',
                    command: () => {
                        this.callDeleteClaim(data);
                    }
                });
            }

            const startDateObj = new Date(data.claimStartDatetime);
            const now = new Date();

            // Get the timestamp for 2 days ago
            const twoDaysAgo = new Date(now.getTime() - (2 * 24 * 60 * 60 * 1000));

            if ((startDateObj > twoDaysAgo) && (data.healthInsuranceSubmission == 'D') &&
                ((data.status === 'A') || (data.status === 'D'))) {
                items.push({
                    label: 'Reverse',
                    class: 'mb-4 mr-4 ml-4 mt-4',
                    command: () => {
                        this.callReverseClaim(data);
                    }
                });

            }

            items.push({
                label: 'Update Status',
                class: 'mb-4 mr-4 ml-4 mt-4',
                command: () => {
                    this.callUpdateStatus(data);
                }
            });

            this.filteredItemsCache.set(data.id, items);

            return items;
        },
        chooseBlankSelection() {
            this.$toast.add({
                severity: 'error',
                summary: 'Error',
                detail: 'Please select a valid option.',
                life: 3000
            });
        }
        ,
        formatAmount(value) {
            if (value) {
                return numeral(String(value)).format('0,0.00');
            }
        },
        async updateClaim() {
            this.submitted = true;
            this.loadingResponse = true;

            if (this.claim.id) {

                if (this.claim.healthInsuranceSubmission === 'E') {
                    try {

                        var newClaimDto = {
                            responseReference: this.claim.responseReference
                        }

                        await ClaimService.updateClaim(this.claim.id, newClaimDto);

                        this.$toast.add({
                            severity: 'success',
                            summary: 'Successful',
                            detail: 'Updated claim',
                            life: 3000
                        });

                        await this.loadData();

                    } catch (error) {
                        this.$toast.add({
                            severity: 'error',
                            summary: 'Error',
                            detail: 'Failed to update claim. Please try again.',
                            life: 3000
                        });
                    }
                }
            }

            this.claim = {};
            this.activeTab = 1;
            this.submitted = false;
            this.loadingResponse = false;
        },
        async submitClaim() {
            this.submitted = true;
            this.loadingResponse = true;

            if (this.claim.id) {

                if (this.confirmationResponse.trim() === "Y") {
                    if (this.claim.healthInsuranceSubmission === 'E') {
                        try {

                            const formData = new FormData();
                            formData.append("file", this.file);

                            await ClaimService.createClaimSubmissionByEmail(
                                this.claim.id, this.resubmissionResponse, formData)

                            // const [responseData] = await Promise.all([
                            //     ClaimService.getClaimsByCompany(this.profile.companyId)
                            // ]);
                            //
                            // this.claims = responseData.data;
                            // this.originalClaims = responseData.data;
                            // this.userStore.setClaims(responseData.data);

                            this.$toast.add({
                                severity: 'success',
                                summary: 'Successful',
                                detail: 'Submit claim by email',
                                life: 3000
                            });

                            await this.loadData();

                            this.resetClientFileInput();

                        } catch (error) {
                            this.$toast.add({
                                severity: 'error',
                                summary: 'Error',
                                detail: 'Failed to submit claim by email. Please try again.',
                                life: 3000
                            });
                        }
                    } else {
                        try {
                            await ClaimService.createClaimSubmissionByEdi(this.claim.id)

                            // const [responseData] = await Promise.all([
                            //     ClaimService.getClaimsByCompany(this.profile.companyId)
                            // ]);
                            //
                            // this.claims = responseData.data;
                            // this.originalClaims = responseData.data;
                            // this.userStore.setClaims(responseData.data);

                            this.$toast.add({
                                severity: 'success',
                                summary: 'Successful',
                                detail: 'Submit claim by EDI',
                                life: 3000
                            });

                            await this.loadData();

                        } catch (error) {
                            this.$toast.add({
                                severity: 'error',
                                summary: 'Error',
                                detail: 'Failed to submit claim by EDI. Please try again.',
                                life: 3000
                            });
                        }
                    }
                }
            }

            this.claim = {};
            this.activeTab = 1;
            this.loadingResponse = false;
            this.submitted = false;
        },
        async submitBenefitCheck() {
            this.submitted = true;
            this.loadingResponse = true;

            if (this.claim.id) {

                if (this.confirmationResponse.trim() === "Y") {
                    try {
                        await ClaimService.createClaimBenefitCheckByEdi(this.claim.id);

                        this.$toast.add({
                            severity: 'success',
                            summary: 'Successful',
                            detail: 'Submit benefit check for claim',
                            life: 3000
                        });

                        await this.loadData();

                    } catch (error) {
                        this.$toast.add({
                            severity: 'error',
                            summary: 'Error',
                            detail: 'Failed to submit benefit check for claim. Please try again.',
                            life: 3000
                        });
                    }
                }
            }

            this.claim = {};
            this.activeTab = 1;
            this.submitted = false;
            this.loadingResponse = false;
        },
        async deleteClaim() {
            this.submitted = true;
            this.loadingResponse = true;

            if (this.confirmationResponse.trim() === "Y") {

                if (this.claim.id) {

                    try {
                        await ClaimService.deleteClaim(this.claim.id);

                        this.$toast.add({
                            severity: 'success',
                            summary: 'Successful',
                            detail: 'Deleted claim',
                            life: 3000
                        });

                        await this.loadData();

                    } catch (error) {
                        this.$toast.add({
                            severity: 'error',
                            summary: 'Error',
                            detail: 'Failed to delete claim. Please try again.',
                            life: 3000
                        });
                    }
                }

            }

            this.claim = {};
            this.activeTab = 1;
            this.submitted = false;
            this.loadingResponse = false;
        },
        async submitClaimReversal() {
            this.submitted = true;
            this.loadingResponse = true;

            if (this.claim.id) {

                try {
                    await ClaimService.createClaimReversal(this.claim.id);

                    this.$toast.add({
                        severity: 'success',
                        summary: 'Successful',
                        detail: 'Updated claim',
                        life: 3000
                    });

                    await this.loadData();

                } catch (error) {
                    this.$toast.add({
                        severity: 'error',
                        summary: 'Error',
                        detail: 'Failed to update claim. Please try again.',
                        life: 3000
                    });
                }
            }

            this.claim = {};
            this.activeTab = 1;
            this.submitted = false;
            this.loadingResponse = false;
        },
        async updateClaimStatus() {
            this.submitted = true;
            this.loadingResponse = true;

            if (this.claim.id) {

                try {
                    await ClaimService.updateClaim(this.claim.id, this.claim)

                    // const [responseData] = await Promise.all([
                    //     ClaimService.getClaimsByCompany(this.profile.companyId)
                    // ]);
                    //
                    // this.claims = responseData.data;
                    // this.originalClaims = responseData.data;
                    // this.userStore.setClaims(responseData.data);

                    this.$toast.add({
                        severity: 'success',
                        summary: 'Successful',
                        detail: 'Updated claim',
                        life: 3000
                    });

                    await this.loadData();

                } catch (error) {
                    this.$toast.add({
                        severity: 'error',
                        summary: 'Error',
                        detail: 'Failed to update claim. Please try again.',
                        life: 3000
                    });
                }
            }

            this.claim = {};
            this.activeTab = 1;
            this.submitted = false;
            this.loadingResponse = false;
        }
    }
}

</script>

<style scoped lang="scss">
a {
    text-decoration: none;
}

::v-deep(.p-datatable-frozen-tbody) {
    font-weight: bold;
}

::v-deep(.p-datatable-scrollable .p-frozen-column) {
    font-weight: bold;
}

::v-deep(.p-progressbar) {
    height: .5rem;
    background-color: #D8DADC;

    .p-progressbar-value {
        background-color: #607D8B;
    }
}

.claim-badge, .submission-badge {
    border-radius: var(--border-radius);
    padding: .25em .5rem;
    text-transform: uppercase;
    font-weight: 700;
    font-size: 12px;
    letter-spacing: .3px;
}

.claim-badge {
    &.status-A {
        background: #C8E6C9;
        color: #256029;
    }

    &.status-I {
        background: #FFCDD2;
        color: #C63737;
    }

    &.status-R {
        background: #FEEDAF;
        color: #8A5340;
    }

    &.status-P {
        background: #B3E5FC;
        color: #23547B;
    }

    &.status-S {
        background: #ECCFFF;
        color: #694382;
    }

    &.status-F {
        background: #ecd1be;
        color: #be5306;
    }

    &.status-D {
        background: #ecd1be;
        color: #be5306;
    }

    &.status-V {
        background: #ECCFFF;
        color: #694382;
    }

    &.status-N {
        background: #C8E6C9;
        color: #256029;
    }

}

.submission-badge {
    &.status-E {
        background: #C8E6C9;
        color: #256029;
    }

    &.status-D {
        background: #FFCDD2;
        color: #C63737;
    }

}

.tabview-custom {
    i, span {
        vertical-align: middle;
    }

    span {
        margin: 0 .5rem;
    }
}

.p-tabview p {
    line-height: 1.5;
    margin: 0;
}

.accordion-custom {
    i, span {
        vertical-align: middle;
    }

    span {
        margin: 0 .5rem;
    }
}

.p-accordion p {
    line-height: 1.5;
    margin: 0;
}

.mb-2 {
    margin-bottom: 1rem;
}

.mr-2 {
    margin-right: 1rem;
}

</style>
